import { useQueryClient } from '@tanstack/react-query';
import { useApi, ApiError } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';

export interface LogCustomerEmailRequest {
  sourceId: string;
  toRecipients: string[];
  ccRecipients?: string[];
  subject: string;
  htmlBody: string;
  templateId?: number;
  templateName?: string;
  isSubjectModified: boolean;
  isBodyModified: boolean;
  attachOutstandingInvoices: boolean;
}

export function useLogCustomerEmail() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, LogCustomerEmailRequest>(
    async (request) => {
      const { sourceId, ...body } = request;
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/customers/${sourceId}/communications/log-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `HTTP error ${response.status}`,
          response.status,
          errorData
        );
      }
      // 201 Created with null body — no need to parse JSON
    },
    () => {
      // Demo mode: no-op, just return void
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        queryClient.invalidateQueries({ queryKey: ["customer-communications"] });
        queryClient.invalidateQueries({ queryKey: ["customer-recent-activities"] });
        queryClient.invalidateQueries({ queryKey: ["customer-audit-logs"] });
      },
    }
  );
}
